import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { MessageDescriptor } from 'react-intl';

import {
  Rarity,
  So5LeaderboardType,
  Sport,
} from '#src/__generated__/globalTypes.ts';
import { FRONTEND_ASSET_HOST } from '#src/constants/assets.ts';

import type {
  formatLineupDisplayName_so5Lineup,
  isSpecialEventSo5LeaderboardType_so5Leaderboard,
  leaderboardHasEntryFees_so5Leaderboard,
  nationalTeamPictureUrl_nationalTeam,
  smallTeamPictureUrl_teamInterface,
  teamPictureUrl_teamInterface,
  validateVaultRequirements_so5Leaderboard,
} from './__generated__/so5.graphql.ts';
import type { TotalAveragePlayerScore } from './averagePlayerScore.ts';
import { dailySwingTournamentTypes } from './baseball.ts';
import { cardAttributes } from './glossary.ts';
import { isType, isTypeFn, withFragments } from './gql/index.ts';
import { dailyHoopsTournamentTypes } from './nba.ts';

export const OPTIMISTIC_SLOTS_COUNT_PER_SPORT: Record<Sport, number> = {
  [Sport.FOOTBALL]: 5,
  [Sport.NBA]: 5,
  [Sport.BASEBALL]: 7,
};

export const formatLineupDisplayName = withFragments(
  (so5Lineup_: formatLineupDisplayName_so5Lineup) => {
    const so5Lineup = so5Lineup_;
    const { name, so5Leaderboard } = so5Lineup;
    const { displayName, trainingCenter } = so5Leaderboard || {};

    if (
      so5Leaderboard?.so5TournamentType.so5LeaderboardType ===
      So5LeaderboardType.SPECIAL_WEEKLY
    ) {
      return so5Leaderboard.so5LeaderboardGroup.displayName;
    }

    return [displayName, trainingCenter && name].filter(Boolean).join(' ');
  },
  {
    so5Lineup: gql`
      fragment formatLineupDisplayName_so5Lineup on So5Lineup {
        id
        name
        so5Leaderboard {
          slug
          displayName
          trainingCenter
          so5TournamentType {
            id
            so5LeaderboardType
          }
          so5LeaderboardGroup {
            slug
            displayName
          }
        }
      }
    ` as TypedDocumentNode<formatLineupDisplayName_so5Lineup>,
  }
);

export enum PlayerScoreStatus {
  NO_GAME = 'no_game',
  DID_NOT_PLAY = 'did_not_play',
  REVIEWING = 'reviewing',
  PENDING = 'pending',
}

export enum FixtureState {
  opened = 'opened',
  started = 'started',
  closed = 'closed',
}

export const isRarityStr = (rarity: Nullable<string>): rarity is Rarity =>
  !rarity || rarity in Rarity;

export const modeTooltipMessages: Record<
  TotalAveragePlayerScore,
  MessageDescriptor
> = {
  LAST_FIVE_SO5_AVERAGE_SCORE: cardAttributes.L5Score,
  LAST_FIFTEEN_SO5_AVERAGE_SCORE: cardAttributes.L15Score,
  LAST_TEN_PLAYED_SO5_AVERAGE_SCORE: cardAttributes.L10Score,
  SEASON_AVERAGE_SCORE: cardAttributes.seasonAverageScore,
  LAST_FORTY_SO5_AVERAGE_SCORE: cardAttributes.L40Score,
};

const nationalTeamPictureUrl = withFragments(
  (nationalTeam_: nationalTeamPictureUrl_nationalTeam) => {
    const nationalTeam = nationalTeam_;
    return `${FRONTEND_ASSET_HOST}/flags/${nationalTeam.country.code}.svg`;
  },
  {
    nationalTeam: gql`
      fragment nationalTeamPictureUrl_nationalTeam on NationalTeam {
        slug
        country {
          slug
          code
        }
      }
    ` as TypedDocumentNode<nationalTeamPictureUrl_nationalTeam>,
  }
);

export const teamPictureUrl = withFragments(
  (team: teamPictureUrl_teamInterface) => {
    if (isType(team, 'Club')) {
      return team.pictureUrl;
    }

    return nationalTeamPictureUrl(team);
  },
  {
    teamInterface: gql`
      fragment teamPictureUrl_teamInterface on TeamInterface {
        slug
        pictureUrl
        ... on NationalTeam {
          slug
          ...nationalTeamPictureUrl_nationalTeam
        }
      }
      ${nationalTeamPictureUrl.fragments.nationalTeam}
    ` as TypedDocumentNode<teamPictureUrl_teamInterface>,
  }
);

export const smallTeamPictureUrl = withFragments(
  (team: smallTeamPictureUrl_teamInterface) => {
    if (isType(team, 'Club')) {
      return team.avatarPictureUrl;
    }

    return nationalTeamPictureUrl(team);
  },
  {
    teamInterface: gql`
      fragment smallTeamPictureUrl_teamInterface on TeamInterface {
        slug
        avatarPictureUrl: pictureUrl(derivative: "avatar")
        ... on NationalTeam {
          slug
          ...nationalTeamPictureUrl_nationalTeam
        }
      }
      ${nationalTeamPictureUrl.fragments.nationalTeam}
    ` as TypedDocumentNode<smallTeamPictureUrl_teamInterface>,
  }
);

export const isSpecialEventSo5LeaderboardType = withFragments(
  (so5Leaderboard?: isSpecialEventSo5LeaderboardType_so5Leaderboard) => {
    return (
      so5Leaderboard &&
      (dailySwingTournamentTypes.includes(
        so5Leaderboard.so5TournamentType.so5LeaderboardType
      ) ||
        dailyHoopsTournamentTypes.includes(
          so5Leaderboard.so5TournamentType.so5LeaderboardType
        ))
    );
  },
  {
    so5Leaderboard: gql`
      fragment isSpecialEventSo5LeaderboardType_so5Leaderboard on So5Leaderboard {
        slug
        so5TournamentType {
          id
          so5LeaderboardType
        }
      }
    ` as TypedDocumentNode<isSpecialEventSo5LeaderboardType_so5Leaderboard>,
  }
);

export const validateVaultRequirements = withFragments(
  (so5Leaderboard?: validateVaultRequirements_so5Leaderboard) => {
    if (!so5Leaderboard) {
      return {
        minVaultBoost: undefined,
        isMissingVaultPoints: false,
        missingVaultPointsCount: 0,
      };
    }
    const minVaultBoost = so5Leaderboard.displayedTypedRules.find(
      isTypeFn('VaultPointsRule')
    )?.minVaultBoost;
    const missingVaultPoints = so5Leaderboard.canCompose?.missingVaultPoints;
    return {
      minVaultBoost,
      isMissingVaultPoints: !!missingVaultPoints,
      missingVaultPointsCount: missingVaultPoints,
    };
  },
  {
    so5Leaderboard: gql`
      fragment validateVaultRequirements_so5Leaderboard on So5Leaderboard {
        slug
        canCompose {
          missingVaultPoints
        }
        displayedTypedRules {
          id
          key
          ... on VaultPointsRule {
            id
            rarity
            minVaultBoost {
              points
              boostLevel {
                milestone
              }
            }
          }
        }
      }
    ` as TypedDocumentNode<validateVaultRequirements_so5Leaderboard>,
  }
);

export const leaderboardHasEntryFees = withFragments(
  (so5Leaderboard?: leaderboardHasEntryFees_so5Leaderboard) =>
    so5Leaderboard?.displayedTypedRules?.some(
      rule => rule.__typename === 'So5EntryItemRule'
    ),
  {
    so5Leaderboard: gql`
      fragment leaderboardHasEntryFees_so5Leaderboard on So5Leaderboard {
        slug
        displayedTypedRules {
          id
          key
          ... on So5EntryItemRule {
            id
            key
          }
        }
      }
    ` as TypedDocumentNode<leaderboardHasEntryFees_so5Leaderboard>,
  }
);
